import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import CategorySelectModal from "@/components/ui/chat/CategorySelectModal";
import { ChatFilter } from "@/components/ui/chat/ChatFilter";
import { getValidAttributeFilters } from "@/components/ui/chat/chatFilterUtils";
import { ChatInput } from "@/components/ui/chat/ChatInput";
import { ChatMessageList } from "@/components/ui/chat/ChatMessageList";
import { VocSidePanel } from "@/components/ui/chat/VocSidePanel";
import { useConversation } from "@/hooks/useConversation";
import { useDisplayGroups } from "@/hooks/useDisplayGroups";
import { Message, MessageType, TMP_CONVERSATION_KEY } from "@/models/conversation";
import { initialConversationState, useChatStore } from "@/stores/ui/useChatStore";
import { DeepAnalysisType, useDeepAnalysisStore } from "@/stores/ui/useDeepAnalysisStore";
import ChatNotification from "../ChatNotification";

export const ChatAnalysis = () => {
  const { t: tCommon } = useTranslation("common");
  const { t: tVoiceek } = useTranslation("voiceek_ui");

  const [isOpenVocPanel, setIsOpenVocPanel] = useState<Record<string, boolean>>({});
  const [isFilterOpen, setIsFilterOpen] = useState<Record<string, boolean>>({});
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const messageRefs = useRef<Record<string, HTMLDivElement>>({});
  const chatInputRef = useRef<HTMLDivElement>(null);
  const chatFilterRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    createConversation,
    postMessage,
    // VoC関連
    selectedMessageId,
    isLoadingMessages,
    correspondingUserMessage,
  } = useConversation();

  const selectedMessageAttributeFilters = correspondingUserMessage?.attributeFilters || {};

  const conversationStates = useChatStore((s) => s.conversationStates);
  const currentConversationId = useChatStore((s) => s.currentConversationId);
  const setConversationState = useChatStore((s) => s.setConversationState);
  const setCurrentConversationState = useChatStore((s) => s.setCurrentConversationState);
  const setCurrentConversationId = useChatStore((s) => s.setCurrentConversationId);

  const openChatModal = useDeepAnalysisStore((s) => s.openChatModal);
  const {
    content,
    isWaitingForAssistantMessage,
    categories,
    vocPeriodStart,
    vocPeriodEnd,
    vocPeriodType,
    attributeFilters,
    tagIds,
  } = conversationStates[currentConversationId] || initialConversationState;

  // フィルター用のデータを取得
  const { displayGroups } = useDisplayGroups();

  // 初期メッセージを表示
  const displayMessages: Message[] = [
    new Message(
      "1",
      MessageType.ASSISTANT,
      tVoiceek("chat.modal.welcome"),
    ),
    ...(messages || []).toReversed(),
  ];
  const shouldSubmitButtonDisabled = !content?.trim() || isWaitingForAssistantMessage;

  const onSuccessPostMessage = (conversationId: string) => {
    // 実行時にgetState()で最新の値を取得
    const { currentConversationId: currentId } = useChatStore.getState();
    const { isDeepAnalysisModalOpen: isModalOpen, deepAnalysisType: analysisType } = useDeepAnalysisStore.getState();

    // レスポンスが返ってきた際に、同じ会話を表示しているときは、toastを表示しない
    if (
      currentId === conversationId && isModalOpen && analysisType === DeepAnalysisType.CHAT
    ) return;

    toast.custom((t) => (
      <ChatNotification
        openChatModal={() => {
          setCurrentConversationId(conversationId);
          openChatModal();
        }}
        toastId={t}
      />
    ), {
      position: "bottom-right",
      unstyled: true,
    });
  };

  const submitChat = async () => {
    if (!content.trim()) return;
    // 入力エリアをクリア
    setIsFilterOpen((prev) => ({ ...prev, [currentConversationId]: false }));

    try {
      if (!currentConversationId) return;

      // periodType が "all" の場合は voc start と voc end に null を送信
      const finalVocPeriodStart = vocPeriodType === "all" ? null : vocPeriodStart;
      const finalVocPeriodEnd = vocPeriodType === "all" ? null : vocPeriodEnd;

      // エラーのないフィルターのみを抽出
      const validAttributeFilters = getValidAttributeFilters(attributeFilters);

      setConversationState(currentConversationId, { content: "" });

      if (currentConversationId === TMP_CONVERSATION_KEY) {
        // 新しい会話を作成
        const { conversationId: newConversationID } = await createConversation(
          content,
          categories,
          finalVocPeriodStart,
          finalVocPeriodEnd,
          onSuccessPostMessage,
          validAttributeFilters,
          tagIds,
        );
        setConversationState(newConversationID, {
          categories,
          vocPeriodType,
          vocPeriodStart: finalVocPeriodStart,
          vocPeriodEnd: finalVocPeriodEnd,
          attributeFilters,
          tagIds,
        });
      } else {
        await postMessage(
          content,
          categories,
          finalVocPeriodStart,
          finalVocPeriodEnd,
          onSuccessPostMessage,
          validAttributeFilters,
          tagIds,
        );
      }
    } catch (e) {
      toast.error(tCommon("errors.unknownError"));
      console.error(e);
    }
  };

  const highlightAndScrollToMessage = (messageId: string) => {
    const messageElement = messageRefs.current[messageId];
    if (!messageElement) return;

    // 関連VoC表示時に対象のメッセージをハイライト
    messageElement.style.borderColor = "#6888E1";
    messageElement.style.backgroundColor = "#D1DDFF";

    setTimeout(() => {
      messageElement.style.backgroundColor = "white";
      messageElement.style.borderColor = "white";
    }, 1000); // 1秒後に元に戻す

    // 対象のメッセージへスクロール
    messageElement.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleViewVocs = (messageId: string) => {
    if (currentConversationId) {
      setIsOpenVocPanel((prev) => ({ ...prev, [currentConversationId]: true }));
    }
    setConversationState(currentConversationId, { selectedMessageId: messageId });
    highlightAndScrollToMessage(messageId);
  };

  const hadleToggleVocPanel = () => {
    if (!currentConversationId) return;

    // VoCパネル開く -> 閉じる ->「お客様の声を参照する」からではなく開くボタンで開いた場合は先程表示していたVoCが残っているため、
    // そのVoCに紐づくメッセージにスクロール&ハイライト
    if (!isOpenVocPanel[currentConversationId] && selectedMessageId) {
      highlightAndScrollToMessage(selectedMessageId);
    }

    setIsOpenVocPanel((prev) => ({ ...prev, [currentConversationId]: !prev[currentConversationId] }));
  };

  const handleToggleFilter = () => {
    if (!currentConversationId) return;
    setIsFilterOpen((prev) => ({ ...prev, [currentConversationId]: !prev[currentConversationId] }));
  };

  const handleCategoryModalSubmit = (_displayGroupId: string, categoryPaths: string[]) => {
    // モーダルで選択されたカテゴリーを直接反映（追加ではなく置き換え）
    setCurrentConversationState({ categories: categoryPaths });
    setIsCategoryModalOpen(false);
  };

  // ChatContainer内で、ChatFilter内とChatInput内以外をクリックしたら閉じる
  useEffect(() => {
    if (!currentConversationId || !isFilterOpen[currentConversationId]) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // ChatContainer内のクリックでない場合は何もしない
      if (!chatContainerRef.current?.contains(target)) {
        return;
      }

      // ChatFilter内またはChatInput内のクリックは無視
      if (
        chatFilterRef.current?.contains(target)
        || chatInputRef.current?.contains(target)
      ) {
        return;
      }

      // ChatContainer内かつChatFilter/ChatInput外がクリックされたので閉じる
      setIsFilterOpen((prev) => ({ ...prev, [currentConversationId]: false }));
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterOpen, currentConversationId]);

  return (
    <div ref={chatContainerRef} className="flex-1 flex w-full min-h-0 overflow-hidden rounded-b-[inherit]">
      <div className="relative flex-1 h-full pb-48 z-1">
        {!isLoadingMessages
          && (
            <ChatMessageList
              messages={displayMessages}
              isChatLoading={isWaitingForAssistantMessage}
              onViewVocs={handleViewVocs}
              messageRefs={messageRefs}
            />
          )}

        <div className="absolute w-full px-8 bottom-8 flex flex-col gap-2 items-center">
          <div className="max-w-[788px] w-full flex flex-col gap-2">
            {isFilterOpen[currentConversationId] && (
              <div ref={chatFilterRef}>
                <ChatFilter
                  onOpenCategoryModal={() => setIsCategoryModalOpen(true)}
                />
              </div>
            )}
            <div ref={chatInputRef}>
              <ChatInput
                onSubmit={submitChat}
                disabled={shouldSubmitButtonDisabled}
                onToggleFilter={handleToggleFilter}
                isFilterOpen={currentConversationId ? isFilterOpen[currentConversationId] : false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* VoCサイドパネル */}
      <VocSidePanel
        isOpen={currentConversationId ? isOpenVocPanel[currentConversationId] || false : false}
        className="z-0"
        onToggle={hadleToggleVocPanel}
        conversationId={currentConversationId || null}
        messageId={selectedMessageId}
        attributeFilters={selectedMessageAttributeFilters}
      />
      {/* カテゴリ選択モーダル */}
      {displayGroups && (
        <CategorySelectModal
          displayGroups={displayGroups}
          initialSelectedPaths={categories || []}
          isOpenModal={isCategoryModalOpen}
          setIsOpenModal={setIsCategoryModalOpen}
          submit={handleCategoryModalSubmit}
        />
      )}
    </div>
  );
};
